'use client'

/**
 * SentimentGauge — the "what's the read on $SYMBOL" signal traders want, on the stock hub. Blends
 * Polygon news sentiment + today's momentum (server-provided, always populated) with live community
 * bull/bear votes (fetched + cast here). Renders SSR-friendly from props, then hydrates the votes.
 */

import { useEffect, useState } from 'react'
import { signIn } from 'next-auth/react'
import { TrendingUp, TrendingDown } from 'lucide-react'
import { computeSentiment } from '@/lib/stocks/sentiment'

export default function SentimentGauge({
  symbol, bullNews, bearNews, neutralNews, dayPct,
}: { symbol: string; bullNews: number; bearNews: number; neutralNews: number; dayPct: number | null }) {
  const [bull, setBull] = useState(0)
  const [bear, setBear] = useState(0)
  const [myVote, setMyVote] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    let alive = true
    fetch(`/api/stocks/sentiment?symbol=${encodeURIComponent(symbol)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (alive && j) { setBull(j.bull || 0); setBear(j.bear || 0); setMyVote(j.myVote ?? null) } })
      .catch(() => {})
    return () => { alive = false }
  }, [symbol])

  const s = computeSentiment({ bullNews, bearNews, neutralNews, dayPct, bullVotes: bull, bearVotes: bear })
  const tone = s.label === 'Bullish' ? 'emerald' : s.label === 'Bearish' ? 'rose' : 'zinc'

  const cast = async (v: 'bull' | 'bear') => {
    if (busy) return
    setBusy(true)
    // optimistic
    const prev = { bull, bear, myVote }
    if (myVote === 'bull') setBull((n) => Math.max(0, n - 1))
    if (myVote === 'bear') setBear((n) => Math.max(0, n - 1))
    if (v === 'bull') setBull((n) => n + 1); else setBear((n) => n + 1)
    setMyVote(v)
    try {
      const res = await fetch('/api/stocks/sentiment', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ symbol, vote: v }) })
      if (res.status === 401) { setBull(prev.bull); setBear(prev.bear); setMyVote(prev.myVote); signIn('google', { callbackUrl: typeof window !== 'undefined' ? window.location.href : '/' }); return }
      if (res.ok) { const j = await res.json(); setBull(j.bull || 0); setBear(j.bear || 0); setMyVote(j.myVote ?? v) }
      else { setBull(prev.bull); setBear(prev.bear); setMyVote(prev.myVote) }
    } catch { setBull(prev.bull); setBear(prev.bear); setMyVote(prev.myVote) }
    finally { setBusy(false) }
  }

  return (
    <section className="rounded-2xl border border-border bg-card p-4 shadow-lg ring-1 ring-border">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-base font-bold tracking-tight text-foreground">
          <span className="h-4 w-1 rounded-full bg-gradient-to-b from-amber-400 to-amber-600" />
          ${symbol} Sentiment
        </h2>
        <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${tone === 'emerald' ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' : tone === 'rose' ? 'bg-rose-500/15 text-rose-600 dark:text-rose-300' : 'bg-muted text-muted-foreground'}`}>
          {s.hasSignal ? s.label : 'No signal yet'}
        </span>
      </div>

      {/* Meter */}
      <div className="relative h-2.5 w-full overflow-hidden rounded-full bg-gradient-to-r from-rose-500/70 via-zinc-600/60 to-emerald-500/70">
        <div className="absolute top-1/2 h-4 w-1.5 -translate-y-1/2 rounded-full bg-white shadow ring-1 ring-black/40" style={{ left: `calc(${s.score}% - 3px)` }} />
      </div>
      <div className="mt-1 flex justify-between text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        <span className="text-rose-600/70 dark:text-rose-400/70">Bearish</span>
        <span className="text-muted-foreground">{s.score}</span>
        <span className="text-emerald-600/70 dark:text-emerald-400/70">Bullish</span>
      </div>

      {/* Breakdown */}
      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-muted-foreground">
        <span>📰 News: <span className="font-semibold text-emerald-700 dark:text-emerald-300">{bullNews}</span> bull · <span className="font-semibold text-rose-600 dark:text-rose-300">{bearNews}</span> bear</span>
        {dayPct != null && <span>Today: <span className={`font-semibold ${dayPct >= 0 ? 'text-emerald-700 dark:text-emerald-300' : 'text-rose-600 dark:text-rose-300'}`}>{dayPct >= 0 ? '+' : ''}{dayPct.toFixed(1)}%</span></span>}
        <span>👥 Community: <span className="font-semibold text-emerald-700 dark:text-emerald-300">{bull}</span> bull · <span className="font-semibold text-rose-600 dark:text-rose-300">{bear}</span> bear</span>
      </div>

      {/* Vote */}
      <div className="mt-3 flex gap-2">
        <button type="button" disabled={busy} onClick={() => cast('bull')}
          className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-bold transition-colors disabled:opacity-60 ${myVote === 'bull' ? 'border-emerald-500/50 bg-emerald-500/20 text-emerald-700 dark:text-emerald-300' : 'border-border bg-muted text-foreground hover:border-emerald-500/40 hover:text-emerald-600 dark:hover:text-emerald-300'}`}>
          <TrendingUp className="h-4 w-4" /> Bullish
        </button>
        <button type="button" disabled={busy} onClick={() => cast('bear')}
          className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-bold transition-colors disabled:opacity-60 ${myVote === 'bear' ? 'border-rose-500/50 bg-rose-500/20 text-rose-600 dark:text-rose-300' : 'border-border bg-muted text-foreground hover:border-rose-500/40 hover:text-rose-600 dark:hover:text-rose-300'}`}>
          <TrendingDown className="h-4 w-4" /> Bearish
        </button>
      </div>
      <p className="mt-1.5 text-[10px] text-muted-foreground">Blend of news sentiment, today’s move, and community votes. Not investment advice.</p>
    </section>
  )
}
